// ─────────────────────────────────────────────────────────────────────────────
// vendorPriceUtils.js
//
// Pure helpers for the "Update Pricing" flow (no React, no network):
//   1. parseVendorSheet()      – turn rows from a vendor price-list Excel into
//                                [{ sku, unitPrice, unit, alts, description }]
//   2. convertVendorPrice()    – vendor unit → our unit. Handles
//                                  • MSF / MLF / MBF  → per SF / LF / BF
//                                  • piece (EA/SH)    → per SF / LF  (÷ coverage)
//                                  • pack (BAG/BOX…)  → per piece    (÷ pack qty)
//                                  • MSF / MLF        → per piece    (× coverage)
//   3. resolveVendorPrice()    – picks the best price for one (vendor row, our
//                                item) pair: family price first, then computed
//                                conversions, and only then the row's second
//                                price (which is the FIRST VARIANT's price)
//   4. compareVendorPrices()   – match vendor SKUs to our vendorPrices[].partNumber
//                                for the selected vendor and diff the prices
//
// IMPORTANT about the vendor's layout: on an item row the first price
// ("Unit Price", e.g. 535 MSF) is the whole family. The second price on the
// same row ("Variant Price", e.g. 14.98 SH) belongs to the FIRST variant only
// (the 7' sheet, the 8' stud …). It is therefore a fallback, never preferred
// over the family price, and never pre-ticked.
// ─────────────────────────────────────────────────────────────────────────────

// ── SKU normalisation ────────────────────────────────────────────────────────
export const normSku = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

const round6 = (n) => Math.round(n * 1e6) / 1e6;

// ── Unit conversion ──────────────────────────────────────────────────────────
// Vendor "multiple" units – price is quoted per N of the base unit.
const MULTI_UNITS = {
  MSF: { base: "SF", div: 1000 }, // per 1,000 sq ft
  MLF: { base: "LF", div: 1000 }, // per 1,000 linear ft
  MBF: { base: "BF", div: 1000 }, // per 1,000 board ft
  CSF: { base: "SF", div: 100 }, // per 100 sq ft
  CLF: { base: "LF", div: 100 }, // per 100 linear ft
};

// Units we store "per single base unit" (0.535 per SF, 0.366 per LF …)
const MEASURE_UOMS = new Set(["SF", "LF", "BF"]);

// Bases that coveragePerUnit can describe (SF or LF per piece). Board feet
// cannot be converted to a piece price from coverage.
const COVERAGE_BASES = new Set(["SF", "LF"]);

const EACH_CLASS = new Set(["EA", "PC", "PCS", "PCE", "EACH", "PIECE"]);

const unitClass = (u) => {
  const x = String(u || "")
    .trim()
    .toUpperCase();
  if (EACH_CLASS.has(x)) return "EA";
  if (x === "SH" || x === "SHEET") return "SHEET";
  if (x === "BNDL" || x === "BDL" || x === "BUNDLE") return "BDL";
  if (x === "CTN" || x === "CARTON") return "CTN";
  if (x === "BX" || x === "BOX") return "BOX";
  if (x === "CS" || x === "CASE") return "CASE";
  if (x === "PK" || x === "PKG" || x === "PACK") return "PK";
  return x;
};

// A single physical piece (a sheet of board is one piece)
const isPieceLike = (cls) => cls === "EA" || cls === "SHEET";

// Units that contain several pieces – need a pack quantity to get to "each"
const PACK_CLASSES = new Set(["BDL", "CTN", "BAG", "BOX", "CASE", "PK"]);

const toPositive = (x) => {
  const n = parseFloat(String(x ?? "").replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Pull a pieces-per-pack number out of a vendor description:
//   "3000 PCS/CTN"  "1000/BOX"  "25/BDL"  "24PC/PK"  "BAG OF 500"  "500 PC"
const PACK_WORDS = "BAG|BOX|CTN|CARTON|CASE|PK|PACK|PKG|BDL|BNDL|BUNDLE";
const PACK_RES = [
  new RegExp(
    `(\\d[\\d,]*)\\s*(?:PCS?|PIECES?|EA|EACH|SCREWS?|PER)?\\s*\\/\\s*(?:${PACK_WORDS})\\b`,
    "i",
  ),
  new RegExp(`(?:${PACK_WORDS})\\s+(?:OF|OF\\s+)\\s*(\\d[\\d,]*)`, "i"),
  /(\d[\d,]*)\s*(?:PCS?|PIECES?|EA)\b/i,
];

export function parsePackQty(desc) {
  const s = String(desc ?? "");
  for (const re of PACK_RES) {
    const m = s.match(re);
    if (m) {
      const n = toPositive(m[1]);
      if (n && n > 1) return n;
    }
  }
  return null;
}

/**
 * Convert one vendor price into the unit we store.
 *
 * @param {number} unitPrice
 * @param {string} vendorUnit
 * @param {string} ourUom
 * @param {object} [ctx]
 * @param {number} [ctx.coverage]    item.coveragePerUnit (SF or LF per piece)
 * @param {number} [ctx.packQty]     pieces per vendor pack (BAG/BOX/…)
 * @param {string} [ctx.packSource]  "saved" | "description"
 *
 * @returns {{ price:number|null, ok:boolean, note:string,
 *             converted:boolean, method:string }}
 *   ok=false       → units don't line up; price is returned raw so the user can
 *                    see it, but it is NOT pre-selected for update.
 *   converted=true → price was COMPUTED using coverage / pack qty (not a plain
 *                    unit match). These are never pre-ticked.
 */
export function convertVendorPrice(unitPrice, vendorUnit, ourUom, ctx = {}) {
  const vu = String(vendorUnit || "")
    .trim()
    .toUpperCase();
  const ours = String(ourUom || "EA")
    .trim()
    .toUpperCase();
  const price = Number(unitPrice);
  const coverage = toPositive(ctx.coverage);
  const packQty = toPositive(ctx.packQty);
  const packTag =
    ctx.packSource === "description"
      ? " (pack qty read from vendor description — verify)"
      : "";

  const res = (p, ok, note, converted = false, method = "") => ({
    price: p,
    ok,
    note,
    converted,
    method,
  });

  if (!Number.isFinite(price)) {
    return res(null, false, "Vendor price is not a number");
  }

  const vcls = unitClass(vu);
  const ocls = unitClass(ours);
  const ourPiece = isPieceLike(ocls);

  // ── Vendor quotes per 100 / 1,000 of a measure (MSF, MLF …) ───────────────
  const multi = MULTI_UNITS[vu];
  if (multi) {
    const perBase = round6(price / multi.div);

    if (multi.base === ours) {
      return res(
        perBase,
        true,
        `${vu} ÷ ${multi.div} → per ${ours}`,
        false,
        "multi",
      );
    }

    // our item is per piece (sheet / each) and we know its coverage (SF or LF)
    if (ourPiece && coverage && COVERAGE_BASES.has(multi.base)) {
      return res(
        round6(perBase * coverage),
        true,
        `${vu} ÷ ${multi.div} × ${coverage} ${multi.base} per ${ours} → per ${ours}`,
        true,
        "coverage",
      );
    }

    return res(
      perBase,
      false,
      `Vendor sells per ${vu}, our unit is ${ours}` +
        (ourPiece && !coverage ? " and the item has no coverage saved" : "") +
        (ourPiece && multi.base === "BF"
          ? " (board feet can't be converted to a piece price)"
          : "") +
        " — check before applying",
    );
  }

  // ── Our item is stored per SF / LF / BF ────────────────────────────────────
  if (MEASURE_UOMS.has(ours)) {
    // vendor already quotes per SF / LF
    if (vcls === ours) return res(round6(price), true, "");

    // coverage is SF/LF per piece – it can't turn a piece price into per-BF
    if (
      COVERAGE_BASES.has(ours) &&
      (isPieceLike(vcls) || PACK_CLASSES.has(vcls))
    ) {
      if (!coverage) {
        return res(
          round6(price),
          false,
          `Vendor sells per ${vu}; item has no coverage per unit saved, so it can't be converted to per ${ours}`,
        );
      }
      if (isPieceLike(vcls)) {
        return res(
          round6(price / coverage),
          true,
          `${vu} price ÷ ${coverage} ${ours} per piece → per ${ours}`,
          true,
          "coverage",
        );
      }
      if (packQty) {
        return res(
          round6(price / packQty / coverage),
          true,
          `${vu} price ÷ ${packQty} pcs ÷ ${coverage} ${ours} per piece → per ${ours}${packTag}`,
          true,
          "pack",
        );
      }
      return res(
        round6(price),
        false,
        `Vendor sells per ${vu}; pack quantity missing`,
      );
    }

    return res(
      round6(price),
      false,
      `Vendor unit is ${vu || "?"}, our unit is ${ours} — check before applying`,
    );
  }

  // ── Our item is per piece / pack / anything that isn't SF / LF / BF ───────
  if (vcls === ocls) return res(round6(price), true, "");

  // each ↔ sheet are the same thing for pricing
  if (isPieceLike(vcls) && ourPiece) {
    return res(round6(price), true, `${vu} treated as ${ours}`);
  }

  // vendor sells a pack (BAG / BOX / …), we store per piece
  if (ourPiece && PACK_CLASSES.has(vcls)) {
    if (packQty) {
      return res(
        round6(price / packQty),
        true,
        `${vu} price ÷ ${packQty} pcs per ${vu} → per ${ours}${packTag}`,
        true,
        "pack",
      );
    }
    return res(
      round6(price),
      false,
      `Vendor sells per ${vu}; pack quantity missing (put it in the description, e.g. 1000/${vu})`,
    );
  }

  return res(
    round6(price),
    false,
    `Vendor unit ${vu || "?"} ≠ our unit ${ours} — check before applying`,
  );
}

/**
 * Work out the best price for one (vendor row, our item) pair.
 *
 * The row's FIRST price is the family price; any further price/unit on the
 * same row is the first variant's price only (e.g. the 7' sheet). Order:
 *   1. family price, plain unit match               (pre-ticked)
 *   2. family price, computed via coverage / pack   (not pre-ticked)
 *   3. second price, plain or computed              (not pre-ticked, flagged
 *                                                    "first variant only")
 *   4. otherwise the family price, flagged as a mismatch
 */
export function resolveVendorPrice(v, item, vp) {
  let packQty = toPositive(vp?.packQty);
  let packSource = packQty ? "saved" : "";
  if (!packQty) {
    const parsed = parsePackQty(v.description);
    if (parsed) {
      packQty = parsed;
      packSource = "description";
    }
  }
  const ctx = {
    coverage: toPositive(item?.coveragePerUnit),
    packQty,
    packSource,
  };

  const family = {
    price: v.unitPrice,
    unit: v.unit,
    alt: false,
    conv: convertVendorPrice(v.unitPrice, v.unit, item?.uom, ctx),
  };
  const alts = (v.alts || []).map((a) => ({
    price: a.price,
    unit: a.unit,
    alt: true,
    conv: convertVendorPrice(a.price, a.unit, item?.uom, ctx),
  }));

  const pick =
    (family.conv.ok && family) ||
    alts.find((c) => c.conv.ok && !c.conv.converted) ||
    alts.find((c) => c.conv.ok) ||
    family;

  const usedAlt = pick.alt;
  const note = usedAlt
    ? [
        `Used the row's ${pick.price.toLocaleString("en-US", {
          maximumFractionDigits: 4,
        })} ${pick.unit} price — that is the vendor's FIRST variant only (check length/size), the ${v.unit} price couldn't be used for our ${
          item?.uom || "EA"
        } unit`,
        pick.conv.note,
      ]
        .filter(Boolean)
        .join(" · ")
    : pick.conv.note;

  const ours = String(item?.uom || "").toUpperCase();
  const newPrice = pick.conv.price;
  const newMsf =
    newPrice !== null && (ours === "SF" || ours === "LF")
      ? round6(newPrice * 1000)
      : null;

  return {
    ...pick.conv,
    // a second-price pick is never trusted enough to pre-tick
    converted: pick.conv.converted || usedAlt,
    method: usedAlt ? "variant" : pick.conv.method,
    note,
    usedAlt,
    vendorPrice: pick.price,
    vendorUnit: pick.unit,
    newMsf,
  };
}

// ── Excel parsing ────────────────────────────────────────────────────────────
// Price cell is either a real number followed by a unit cell (535 | "MSF"),
// or a single text cell such as "2,209.00      MSF".
const UNIT_RE = /^[A-Za-z]{1,6}$/;
const MONEY_UNIT_RE = /^\s*\$?\s*([\d,]+(?:\.\d+)?)\s+([A-Za-z]{1,6})\s*$/;
const NUM_RE = /^\s*\$?\s*[\d,]+(?:\.\d+)?\s*$/;

const toNum = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && NUM_RE.test(v))
    return parseFloat(v.replace(/[$,\s]/g, ""));
  return null;
};

const clean = (v) =>
  v === null || v === undefined ? "" : String(v).replace(/\s+/g, " ").trim();

// Every price/unit pair on the row, left to right. First = family price.
function findPrices(row) {
  const out = [];
  // start at col 2 so a numeric "Variant" value (7, 8, 9 …) in col 1 is never
  // mistaken for the price
  for (let j = 2; j < row.length; j++) {
    const c = row[j];
    if (typeof c === "string") {
      const m = c.match(MONEY_UNIT_RE);
      if (m) {
        out.push({
          price: parseFloat(m[1].replace(/,/g, "")),
          unit: m[2].toUpperCase(),
          idx: j,
        });
        continue;
      }
    }
    const n = toNum(c);
    if (n !== null) {
      const u = row[j + 1];
      if (typeof u === "string" && UNIT_RE.test(u.trim())) {
        out.push({ price: n, unit: u.trim().toUpperCase(), idx: j });
        j++; // unit cell consumed
      }
    }
  }
  return out;
}

// Alternate prices = other units on the row (same unit as the family price is
// ignored, that's usually a second price column like "list" vs "net")
const toAlts = (prices) => {
  const primary = prices[0];
  const seen = new Set([primary.unit]);
  const alts = [];
  for (const p of prices.slice(1)) {
    if (seen.has(p.unit)) continue;
    seen.add(p.unit);
    alts.push({ price: p.price, unit: p.unit });
  }
  return alts;
};

// Some rows are exported as ONE text cell (with or without line breaks):
//   "12DS      05      5' X 1/2" DENSSHIELD      1,646.00      MSF      32.92      SH"
//   "GRA-H2      GRABBER EYE LAG 2" - 2M      479.80      BOX"
const TEXT_LINE_RE =
  /^(\S+)\s+(?:.*?\s)?([\d,]+\.\d{2})\s+([A-Za-z]{2,5})(?=\s|$)/;
const TEXT_PAIR_RE = /([\d,]+\.\d{2})\s+([A-Za-z]{2,5})(?=\s|$)/g;

function parseTextLine(line) {
  const t = line.trim();
  if (!t || /^item\s/i.test(t)) return null;
  const m = t.match(TEXT_LINE_RE);
  if (!m) return null;
  const desc = t
    .slice(m[1].length, t.indexOf(m[2], m[1].length))
    .replace(/\s+/g, " ")
    .trim();

  const primary = {
    price: parseFloat(m[2].replace(/,/g, "")),
    unit: m[3].toUpperCase(),
  };
  const rest = t.slice(m[0].length);
  const extra = [];
  for (const p of rest.matchAll(TEXT_PAIR_RE)) {
    extra.push({
      price: parseFloat(p[1].replace(/,/g, "")),
      unit: p[2].toUpperCase(),
    });
  }

  return {
    sku: m[1],
    unitPrice: primary.price,
    unit: primary.unit,
    alts: toAlts([primary, ...extra]),
    description: desc,
  };
}

// The Item column sometimes also holds the description, separated by a long
// run of spaces: "UC-TLS142K          UCAN 1/4" X 2" EYE LAG …"
function splitSkuCell(raw) {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\S+)\s{3,}(\S[\s\S]*)$/);
  return m
    ? { sku: m[1], extraDesc: m[2].replace(/\s+/g, " ").trim() }
    : { sku: clean(s), extraDesc: "" };
}

/**
 * @param {Array<Array<any>>} rows  2-D array (sheet_to_json with header:1)
 * @returns {{ items:Array, variantRowsIgnored:number, duplicates:string[] }}
 */
export function parseVendorSheet(rows, { sheetName = "" } = {}) {
  const items = [];
  const seen = new Map(); // normSku -> index in items
  const duplicates = [];
  let variantRowsIgnored = 0;

  const push = (entry) => {
    const key = normSku(entry.sku);
    if (!key) return;
    if (seen.has(key)) {
      duplicates.push(entry.sku);
      items[seen.get(key)] = entry; // last one wins
      return;
    }
    seen.set(key, items.length);
    items.push(entry);
  };

  rows.forEach((row, i) => {
    if (!Array.isArray(row) || row.every((c) => c === null || c === "")) return;
    const rowNum = i + 1;
    const first = row[0];

    const prices = findPrices(row);

    // Whole line in ONE text cell (line breaks or not)
    if (
      typeof first === "string" &&
      (first.includes("\n") ||
        (!prices.length && TEXT_LINE_RE.test(first.trim())))
    ) {
      first.split(/\r?\n/).forEach((line) => {
        const parsed = parseTextLine(line);
        if (parsed) push({ ...parsed, sheetName, row: rowNum });
      });
      return;
    }

    if (!prices.length) return; // headings, column headers, blank spacers …
    const hit = prices[0];

    const { sku, extraDesc } = splitSkuCell(first);
    if (!sku || /^item$/i.test(sku)) {
      // price present but no Item number → variant row
      if (!sku) variantRowsIgnored++;
      return;
    }

    // Description = longest text cell between the item column and the price
    let description = extraDesc;
    for (let j = 1; j < hit.idx; j++) {
      const c = clean(row[j]);
      if (typeof row[j] === "string" && c.length > description.length)
        description = c;
    }

    push({
      sku,
      unitPrice: hit.price,
      unit: hit.unit,
      alts: toAlts(prices),
      description,
      sheetName,
      row: rowNum,
    });
  });

  return { items, variantRowsIgnored, duplicates };
}

// ── Comparison ───────────────────────────────────────────────────────────────
const idOf = (v) => String(v?._id || v || "");
const PRICE_EPS = 0.00005;
export const BIG_CHANGE_PCT = 25;

/**
 * @param {object}  args
 * @param {Array}   args.parsedItems  output of parseVendorSheet().items
 * @param {Array}   args.catalog      ItemDatabase docs (vendorPrices populated or raw ids)
 * @param {string}  args.vendorId     selected vendor Account _id
 */
export function compareVendorPrices({ parsedItems, catalog, vendorId }) {
  const vid = String(vendorId);

  // Index OUR items for this vendor by normalised SKU (vendorPrices[].partNumber)
  const ourBySku = new Map();
  let ourVendorItems = 0;
  let ourWithoutSku = 0;

  for (const item of catalog) {
    const vp = (item.vendorPrices || []).find((v) => idOf(v.vendor) === vid);
    if (!vp) continue;
    ourVendorItems++;
    const key = normSku(vp.partNumber);
    if (!key) {
      ourWithoutSku++;
      continue;
    }
    if (!ourBySku.has(key)) ourBySku.set(key, []);
    ourBySku.get(key).push({ item, vp });
  }

  const rows = [];
  const unmatchedVendor = [];
  const matchedKeys = new Set();

  for (const v of parsedItems) {
    const key = normSku(v.sku);
    const matches = ourBySku.get(key);
    if (!matches) {
      unmatchedVendor.push(v);
      continue;
    }
    matchedKeys.add(key);

    for (const { item, vp } of matches) {
      const oldPrice = Number(vp.price || vp.unitRate || 0);
      const conv = resolveVendorPrice(v, item, vp);
      const newPrice = conv.price;
      const diff = newPrice === null ? null : round6(newPrice - oldPrice);
      const pct =
        newPrice === null || !oldPrice
          ? null
          : Math.round(((newPrice - oldPrice) / oldPrice) * 10000) / 100;

      let status;
      if (newPrice === null) status = "invalid";
      else if (!conv.ok) status = "mismatch";
      else if (Math.abs(diff) < PRICE_EPS) status = "same";
      else status = diff > 0 ? "increase" : "decrease";

      rows.push({
        key: `${item._id}`,
        itemId: item._id,
        itemCode: item.itemCode,
        description: item.description,
        size: item.size || "",
        uom: item.uom || "EA",
        sku: v.sku,
        vendorDescription: v.description,
        vendorPrice: conv.vendorPrice,
        vendorUnit: conv.vendorUnit,
        newPrice,
        newMsf: conv.newMsf,
        oldPrice,
        diff,
        pct,
        bigChange: pct !== null && Math.abs(pct) >= BIG_CHANGE_PCT,
        status,
        converted: !!conv.converted,
        convMethod: conv.method || "",
        usedAlt: !!conv.usedAlt,
        note: conv.note,
      });
    }
  }

  // Our items (for this vendor) whose SKU never showed up in the file
  const missingFromFile = [];
  for (const [key, list] of ourBySku) {
    if (matchedKeys.has(key)) continue;
    for (const { item, vp } of list) {
      missingFromFile.push({
        key: `${item._id}`,
        itemCode: item.itemCode,
        description: item.description,
        uom: item.uom || "EA",
        sku: vp.partNumber,
        oldPrice: Number(vp.price || vp.unitRate || 0),
      });
    }
  }

  const count = (s) => rows.filter((r) => r.status === s).length;
  return {
    rows,
    unmatchedVendor,
    missingFromFile,
    summary: {
      vendorRows: parsedItems.length,
      ourVendorItems,
      ourWithoutSku,
      matched: rows.length,
      increase: count("increase"),
      decrease: count("decrease"),
      same: count("same"),
      mismatch: count("mismatch") + count("invalid"),
      converted: rows.filter((r) => r.converted).length,
      unmatchedVendor: unmatchedVendor.length,
      missingFromFile: missingFromFile.length,
    },
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// Description similarity  (for vendor rows whose SKU is not on any of our items)
// ═════════════════════════════════════════════════════════════════════════════
//
// Construction-aware normalisation so that
//   vendor: 8' - 1 5/8" STUD - 1 1/4" FL - 18MIL/25GA
//   ours  : 1-5/8 Steel Stud 25ga 1-1/4 Flange
// end up with the same spec tokens (1_5/8, 1_1/4, g25).
//
// Lengths (7' 8' 10' 12') are deliberately DROPPED: on the vendor's item row
// they are just the first variant, and the price is a family unit price.

const GAUGE_BY_MIL = { 18: 25, 33: 20, 43: 18, 54: 16, 68: 14, 97: 12 };

const WORD_ALIASES = {
  std: "standard",
  stnd: "standard",
  fc: "firecode",
  trk: "track",
  chnl: "channel",
  chan: "channel",
  ch: "channel",
  dw: "drywall",
  gyp: "gypsum",
  gb: "gypsum",
  flng: "flange",
  fl: "flange",
  leg: "flange",
  lt: "lite",
  light: "lite",
  insul: "insulation",
  wht: "white",
  galv: "galvanized",
  gal: "galvanized",
  scr: "screw",
  scrw: "screw",
  ss: "stud",
  cgc: "cgc",
};

const STOP_WORDS = new Set([
  "the",
  "and",
  "of",
  "for",
  "with",
  "x",
  "in",
  "a",
  "an",
  "to",
  "by",
  "pc",
  "pcs",
  "per",
  "ea",
  "each",
  "ctn",
  "box",
  "bag",
  "pk",
  "pack",
  "bdl",
  "bndl",
  "roll",
  "case",
  "carton",
  "approx",
]);

export function normalizeDesc(str) {
  let s = String(str ?? "").toLowerCase();
  s = s.replace(/[“”″˝]/g, '"').replace(/[’′]/g, "'");
  // lengths:  8'  8 ft  8-foot  → "8ft"
  s = s.replace(/(\d+(?:\.\d+)?)\s*(?:'|ft\b|foot\b|feet\b)/g, "$1ft ");
  // mixed fractions "1 5/8" / "1-5/8" → "1_5/8"
  s = s.replace(/(\d+)[\s-]+(\d+)\/(\d+)/g, "$1_$2/$3");
  // mil → gauge  (18 MIL = 25 GA …)
  s = s.replace(/(\d+)\s*mils?\b/g, (_, n) =>
    GAUGE_BY_MIL[n] ? ` g${GAUGE_BY_MIL[n]} ` : ` mil${n} `,
  );
  s = s.replace(/(\d+)\s*(?:ga|gauge)\b/g, " g$1 ");
  s = s.replace(/["']/g, " ");
  s = s.replace(/[^a-z0-9_/.\s]/g, " ");
  return s.replace(/\s+/g, " ").trim();
}

const SPEC_TOKEN_RE = /^(?:\d+_\d+\/\d+|\d+\/\d+|\d+\.\d+|\d+|g\d+)$/;

export function extractSpecs(str) {
  const specs = new Set();
  let gauge = null;
  for (const t of normalizeDesc(str).split(" ")) {
    if (!t) continue;
    if (/^g\d+$/.test(t)) {
      gauge = t;
      specs.add(t);
    } else if (SPEC_TOKEN_RE.test(t)) {
      specs.add(t);
    }
    // "8ft" etc. are intentionally not matched → dropped
  }
  return { specs, gauge };
}

function wordSet(str) {
  const out = new Set();
  for (let t of normalizeDesc(str).split(" ")) {
    if (!t || SPEC_TOKEN_RE.test(t) || /^\d+ft$/.test(t) || /^mil\d+$/.test(t))
      continue;
    t = WORD_ALIASES[t] || t;
    if (t.length < 2 || STOP_WORDS.has(t)) continue;
    if (t.length > 3 && t.endsWith("s")) t = t.slice(0, -1); // crude plural
    out.add(t);
  }
  return out;
}

const dice = (a, b) => {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  return (2 * inter) / (a.size + b.size);
};

const jaccard = (a, b) => {
  if (!a.size && !b.size) return 1;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  return inter / (a.size + b.size - inter);
};

// Dimensions explicitly written in INCHES (10" , 2-1/2" , 54"). A number with a
// foot mark (10') is a length and is ignored, so "10' track" is NOT a 10" track.
// Every inch dimension on one side must also appear on the other side.
const INCH_RE = /(\d+[\s-]+\d+\/\d+|\d+\/\d+|\d*\.\d+|\d+)\s*["\u201d\u2033]/g;

export function extractInchDims(str) {
  const out = new Set();
  const s = String(str ?? "");
  for (const m of s.matchAll(INCH_RE)) {
    out.add(m[1].trim().replace(/[\s-]+(\d+)\/(\d+)/, "_$1/$2"));
  }
  return out;
}

/**
 * Similarity between a vendor description and one of our items.
 * @returns {{ score:number, specOk:boolean, specNote:string }}
 */
export function scoreDescription(vendorDesc, itemText) {
  const vw = wordSet(vendorDesc);
  const iw = wordSet(itemText);
  const vs = extractSpecs(vendorDesc);
  const is = extractSpecs(itemText);

  const wordScore = dice(vw, iw);
  const bothHaveSpecs = vs.specs.size > 0 && is.specs.size > 0;
  const specScore = bothHaveSpecs ? jaccard(vs.specs, is.specs) : 0.5;

  const gaugeConflict = !!(vs.gauge && is.gauge && vs.gauge !== is.gauge);
  // Every spec on the shorter side must appear on the other side
  // (1-5/8 stud ≠ 3-5/8 stud, 1/2" board ≠ 5/8" board). Extra numbers on one
  // side only (e.g. "3000 PCS/CTN") are tolerated.
  let subset = true;
  if (bothHaveSpecs) {
    const [small, large] =
      vs.specs.size <= is.specs.size
        ? [vs.specs, is.specs]
        : [is.specs, vs.specs];
    for (const x of small) if (!large.has(x)) subset = false;
  }
  // inch dimensions written with a " mark must exist on the other side
  const missingDim = (dims, otherSpecs) =>
    [...dims].find((d) => !otherSpecs.has(d));
  const dimConflict =
    missingDim(extractInchDims(vendorDesc), is.specs) ||
    missingDim(extractInchDims(itemText), vs.specs);

  const specOk =
    !gaugeConflict &&
    !dimConflict &&
    (!bothHaveSpecs || (subset && specScore >= 0.5));

  let specNote = "";
  if (gaugeConflict) specNote = `Gauge differs (${vs.gauge} vs ${is.gauge})`;
  else if (dimConflict) specNote = `Size ${dimConflict}" is on one side only`;
  else if (!specOk) specNote = "Sizes / specs differ";

  return {
    score: Math.round((0.6 * wordScore + 0.4 * specScore) * 1000) / 1000,
    specOk,
    specNote,
  };
}

const itemText = (item) =>
  [item.description, item.size, item.manufacturer].filter(Boolean).join(" ");

/**
 * For each unmatched vendor row, find the closest of our items.
 *
 * @param {object} args
 * @param {Array}  args.unmatched         vendor rows with no SKU match
 * @param {Array}  args.catalog           our items
 * @param {Set}    args.excludeItemIds    items already matched by SKU this run
 * @param {Set}    args.preferItemIds     items whose saved SKU wasn't in the file
 *                                        (likely "vendor renumbered it") → small boost
 * @returns {Array<{ vendorRow, candidates:[{ item, score, specOk, specNote }] }>}
 */
export function suggestCandidates({
  unmatched,
  catalog,
  excludeItemIds = new Set(),
  preferItemIds = new Set(),
  topN = 5,
  minScore = 0.3,
}) {
  const pool = catalog
    .filter((it) => !excludeItemIds.has(String(it._id)))
    .map((it) => ({ item: it, text: itemText(it), id: String(it._id) }));

  const out = [];
  for (const v of unmatched) {
    const scored = [];
    for (const p of pool) {
      const r = scoreDescription(v.description, p.text);
      let score = r.score;
      if (preferItemIds.has(p.id)) score = Math.min(1, score + 0.1);
      if (score >= minScore) scored.push({ item: p.item, ...r, score });
    }
    scored.sort((a, b) => b.score - a.score);
    if (scored.length)
      out.push({ vendorRow: v, candidates: scored.slice(0, topN) });
  }
  return out;
}

/**
 * Turn "vendor row X ≈ our item Y" into a table row.
 */
export function buildSuggestionRow({
  vendorRow: v,
  item,
  vendorId,
  confidence,
  reason,
  source,
  specOk,
  specNote,
}) {
  const vid = String(vendorId);
  const vp = (item.vendorPrices || []).find((x) => idOf(x.vendor) === vid);
  const oldPrice = vp ? Number(vp.price || vp.unitRate || 0) : null;
  const conv = resolveVendorPrice(v, item, vp);
  const newPrice = conv.price;
  const diff =
    newPrice === null || oldPrice === null ? null : round6(newPrice - oldPrice);
  const pct =
    newPrice === null || !oldPrice
      ? null
      : Math.round(((newPrice - oldPrice) / oldPrice) * 10000) / 100;

  const oldSku = vp ? String(vp.partNumber || "") : "";
  const notes = [];
  if (conv.note && (!conv.ok || conv.converted || conv.usedAlt))
    notes.push(conv.note);
  if (!specOk && specNote) notes.push(specNote);
  if (!vp)
    notes.push("Item has no price entry for this vendor — one will be added");

  return {
    key: `s:${item._id}:${normSku(v.sku)}`,
    kind: "suggestion",
    itemId: item._id,
    itemCode: item.itemCode,
    description: item.description,
    size: item.size || "",
    uom: item.uom || "EA",
    sku: v.sku,
    oldSku,
    skuChanged: !!vp && !!oldSku && normSku(oldSku) !== normSku(v.sku),
    hasVendorEntry: !!vp,
    vendorDescription: v.description,
    vendorPrice: conv.vendorPrice,
    vendorUnit: conv.vendorUnit,
    newPrice,
    newMsf: conv.newMsf,
    oldPrice,
    diff,
    pct,
    bigChange: pct !== null && Math.abs(pct) >= BIG_CHANGE_PCT,
    unitOk: conv.ok,
    converted: !!conv.converted,
    convMethod: conv.method || "",
    usedAlt: !!conv.usedAlt,
    specOk,
    // deterministic spec conflict beats the AI's opinion
    confidence: Math.max(
      0,
      Math.min(specOk ? 100 : 60, Math.round(confidence)),
    ),
    reason: reason || "",
    source, // "ai" | "local"
    note: notes.join(" · "),
    conflict: false,
  };
}

/** Rows we're comfortable pre-ticking: AI-confirmed, specs agree, units agree,
 *  and the price didn't need a computed (coverage / pack) conversion. */
export const AUTO_SELECT_CONFIDENCE = 95;
export const isAutoSelectable = (s) =>
  s.source === "ai" &&
  s.confidence >= AUTO_SELECT_CONFIDENCE &&
  s.specOk &&
  s.unitOk &&
  !s.converted &&
  s.newPrice !== null &&
  !s.conflict;
